define((require) => {
    "use strict";

    var ImprovedView    = require("editor/zbone/ImprovedView"),
        ViewScope       = require("shared/ui/common/ViewScope"),
        Marionette      = require("marionette"),
        _               = require("underscore");

    /**
     *   collection view for rendering a <tt>ChunkingCollection</tt>.
     *
     * @see  ChunkingCollection
     *
     * @class ChunkingCollectionView
     */
    return ImprovedView.extend({

        /**
         * CSS class name to be applied to newly added children, defaults to <tt>fade-in</tt>.
         *
         * @type {String}
         */
        entryTransition: "fade-in",

        /**
         * The item views class/constructor function.
         * @type {Function}
         */
        itemView: null,

        /**
         * Factory function for providing additional options to the constructed item view.
         *
         * @type {Object|Function}
         */
        itemViewOptions: null,

        /**
         * Internal array of all views rendered by this collection view - including potential pseudo views (section headers etc) returned
         * by <tt>onAddingView</tt>.
         *
         * @type {ImprovedView[]}
         */
        _views: null,

        /**
         * Internal array of all item views rendered by this collection view, i.e. the actual views representing the
         * models contained in the associated collection, and not potential pseudo views (section headers etc).
         *
         * @type {ImprovedView[]}
         */
        _itemViews: null,

        /**
         * Constructor.
         *
         * @param {Object} options @cfg itemView {Function}
         */
        constructor: function(options) {
            this.__super();

            this._views = [];
            this._itemViews = [];
            this.collection = this.options.collection;
            this.itemView = options.itemView || this.itemView;
            this.itemEvents = options.itemEvents || this.itemEvents;
            this.itemViewOptions = options.itemViewOptions || this.itemViewOptions;

            this.listenTo(this.collection, {
                "add": this._addHandler,

                "remove": this._removeHandler,

                "reset": function() {
                    this._closeAndRemoveChildren();
                },

                "sync": function() {
                    if (this.collection.isEmpty()) {
                        this._handleOnEmpty();
                    }
                }
            });
        },

        template: function() {
            return "";
        },

        serializeData: function() {
            return {};
        },

        onShow: function() {
            if (this.collection.isEmpty() && !this.collection.isPending()) {
                this._handleOnEmpty();
            } else {
                this._addHandler(this.collection.latest());
            }
        },

        onEmpty: function() {
        },

        removeView: function(view) {
            var i = this._views.indexOf(view);

            view.isClosing = true;

            this._views.splice(i, 1);

            i = this._itemViews.indexOf(view);

            if (i !== -1) {
                this._itemViews.splice(i, 1);
            }

            Marionette.triggerMethod.call(view, "closing");

            if (!view || view.isClosed) {
                return;
            }

            if (view.close) {
                view.close();
            } else if (view.remove) {
                view.remove();
            }

            view.$el.remove();
        },

        at: function(index) {
            return this._views[index];
        },

        findById: function(id) {
            return _.find(this._itemViews, (itemView) => {
                return itemView.model.get("id") === id;
            });
        },

        numberOfViews: function() {
            return this._views.length;
        },

        _addView: function(view, index) {
            var views = _.isArray(view) ? view : [view],
                usingIndex = index !== undefined;
            views.forEach((view) => {
                this._register(view);

                view.render();

                if (usingIndex) {
                    this._views.splice(index, 0, view);

                    var previousView = this.at(index - 1);

                    if (previousView) {
                        view.$el.insertAfter(previousView.el);
                    } else {
                        this.$el.prepend(view.el);
                    }

                    index++;
                } else {
                    this.$el.append(view.el);
                    this._views.push(view);
                }

                Marionette.triggerMethod.call(view, "show");

                view.$el.addClass(this.entryTransition);
            });
        },

        _handleOnEmpty: function() {
            var children = this.onEmpty();

            if (children) {
                this._addView(children);
            }
        },

        _register: function(view) {
            view.parentView = this;
            ViewScope.register(this, view);
        },

        _listenForItemViewEvents: function(view) {
            if (this.itemEvents) {
                var self = this;

                _.each(this.itemEvents, (callback, event) => {
                    self.listenTo(view, event, callback.bind(self));
                });
            }
        },

        /**
         * @private
         *
         * Invoked when a new model is added to the collection.
         *
         * Creates and renders the view representing said model, and adds it to the DOM.
         *
         * @param {Model|Model[]} models A model of array of models.
         * @param {Number} index  The position of the model in the collection.
         */
        _addHandler: function(models, index) {
            this.onBeforeViewsAdded();

            // If theres already a view occupying the specified index, we assume that the view was
            // added manually, i.e. by calling <tt>collection.add(model, 0)</tt>. We need to determine
            // the proper index, taken potential pseudo views into account.
            var viewAtIndex = this.at(index);

            if (viewAtIndex) {
                // Get the item view currently at that position
                var itemView = this._itemViews[index];

                if (itemView) {
                    // Lookup the rendered index of the item view and set it as the starting index...
                    index = this._views.indexOf(itemView);
                }
            }

            models.forEach((model) => {
                var viewOptions = _.extend(this._getItemViewOptions(), {model: model}),
                    view = new this.itemView(viewOptions),
                    i = (index !== undefined) ? index : this._views.length,
                    prependedView = this.onAddingView(view, i);

                if (prependedView) {
                    this._addView(prependedView, i);

                    i += _.isArray(prependedView) ? prependedView.length : 1;
                }

                this._listenForItemViewEvents(view);
                this._addView(view, i);
                this._itemViews.splice(i, 0, view);
            });

            this.onViewsAdded();
        },

        _getItemViewOptions: function() {
            if (_.isFunction(this.itemViewOptions)) {
                return this.itemViewOptions.call(this);
            }

            return this.itemViewOptions || {};
        },

        _removeHandler: function(model) {
            var view = _.find(this._views, (view) => {
                return view.model.get("id") === model.get("id");
            });

            if (view) {
                this.removeView(view);
            }
        },

        _closeChildren: function() {
            _.invoke(this._views, "close");
        },

        _closeAndRemoveChildren: function() {
            this._views.forEach((view) => {
                view.close();
                view.$el.remove();
            });

            this._views = [];
            this._itemViews = [];
        },

        onBeforeViewsAdded: function() {

        },

        onAddingView: function(index, model) { // eslint-disable-line no-unused-vars

        },

        onViewsAdded: function() {

        },

        onClose: function() {
            this._closeChildren();

            this._views = null;
            this._itemViews = null;
        }

    });
});



// WEBPACK FOOTER //
// ./src/editor/ui/common/ChunkingCollectionView.js